import type { RailLayout } from "@/data/rail-branches";
import { MARKER_DIAMETER } from "../../marker/MarkerMetrics/metrics";
import type { MarkerCircleOffset } from "@/lib/timeline/scene/SceneMotion/motion";

/** How long the two markers take to come together, and to part again. */
export const ACQUIRE_EASE_MS = 900;

type Seat = (characterId: string, playheadMs: number) => MarkerCircleOffset | null;

const smooth = (t: number): number => {
  const clamped = Math.min(1, Math.max(0, t));
  return clamped * clamped * (3 - 2 * clamped);
};

/** How far along coming together they are: 0 apart, 1 touching. */
export function acquireClosenessAt(startMs: number, endMs: number, playheadMs: number): number {
  if (playheadMs < startMs || playheadMs >= endMs) {
    return 0;
  }
  return Math.min(
    smooth((playheadMs - startMs) / ACQUIRE_EASE_MS),
    smooth((endMs - playheadMs) / ACQUIRE_EASE_MS),
  );
}

/** Someone taking DNA and the animal meet side by side, so neither covers the other's name. */
export function acquireShift(
  characterId: string,
  playheadMs: number,
  rail: Pick<RailLayout, "acquisitions">,
  seat: Seat,
): MarkerCircleOffset {
  const none = { dx: 0, dy: 0 };
  const found = rail.acquisitions?.find((item) => (
    (item.characterId === characterId || item.fromCharacterId === characterId)
    && item.startMs <= playheadMs
    && playheadMs < item.endMs
  ));
  if (found === undefined) {
    return none;
  }
  const mine = seat(characterId, playheadMs);
  const otherId = found.characterId === characterId ? found.fromCharacterId : found.characterId;
  const other = seat(otherId, playheadMs);
  if (mine === null || other === null) {
    return none;
  }
  const onLeft = mine.dx === other.dx ? characterId < otherId : mine.dx < other.dx;
  const close = acquireClosenessAt(found.startMs, found.endMs, playheadMs);
  const targetDx = (mine.dx + other.dx) / 2 + (onLeft ? -1 : 1) * (MARKER_DIAMETER / 2);
  const targetDy = (mine.dy + other.dy) / 2;
  return { dx: (targetDx - mine.dx) * close, dy: (targetDy - mine.dy) * close };
}
